import { useState, useRef, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { chatWidgetStore } from 'shared/stores/chatWidgetStore'
import useKodaSocket from './hooks/useKodaSocket'
import { kodaContextForSend } from './kodaContext'
import MessageBubble, { StreamingBubble } from './components/MessageBubble'
import ConfirmationCard from './components/ConfirmationCard'
import ActivityCard from './components/ActivityCard'
import FollowUpSuggestions from './components/FollowUpSuggestions'
import QuickActions from './components/QuickActions'
import ThinkingIndicator from './components/ThinkingIndicator'
import ChatPopover from './components/ChatPopover/ChatPopover'
import kodaLogo from '../../assets/img/koda.png'
import kodaLogoAsk from '../../assets/img/Koda-ask.png'
import { ReactComponent as MicIcon } from '../../assets/icons/chat/mic.svg'
import { ReactComponent as SendIcon } from '../../assets/icons/chat/plane.svg'
// import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg'
import { ReactComponent as CrossIcon } from '../../assets/icons/cross.svg'
import { ReactComponent as StarsIcon } from '../../assets/icons/stars-2.svg'
import { ReactComponent as MagicIcon } from '../../assets/icons/magic.svg'
import { ReactComponent as BlocksIcon } from '../../assets/icons/blocks.svg'
import s from './styles/ChatWidget.module.scss'

const messageTemplate = {
	'/': {
		message: 'Hey! How can I help you today?',
		userMessage: 'Hey! Can you help me?',
		time1: 3000,
		time2: 6000,
	},
	default: {
		message: 'Hey! How can I help you?',
		userMessage: 'Hey! Can you help me?',
		time1: 3000,
		time2: 6000,
	},
}

function ChatWidget() {
	const isOpen = chatWidgetStore.isOpen
	const setIsOpen = useCallback(next => {
		if (next) chatWidgetStore.open()
		else chatWidgetStore.close()
	}, [])
	const [inputText, setInputText] = useState('')
	const [showPopover, setShowPopover] = useState(false)
	const [showBadge, setShowBadge] = useState(false)
	const [, setTypingComplete] = useState(false)
	const [showNewChatQuickActions, setShowNewChatQuickActions] = useState(true)
	const messagesEndRef = useRef(null)
	const inputRef = useRef(null)
	const location = useLocation()

	// Voice input state
	const [isListening, setIsListening] = useState(false)
	const [isMicSupported, setIsMicSupported] = useState(false)
	const recognitionRef = useRef(null)
	/** Committed final transcript this session; display = committed + interim to avoid duplicate/patchy text */
	const committedRef = useRef('')
	/** True while user wants dictation until second mic click or send */
	const listeningIntentRef = useRef(false)
	const recognitionRestartTimerRef = useRef(null)

	const clearRecognitionRestart = useCallback(() => {
		if (recognitionRestartTimerRef.current) {
			clearTimeout(recognitionRestartTimerRef.current)
			recognitionRestartTimerRef.current = null
		}
	}, [])

	const {
		connectionStatus,
		isChatUnavailable,
		messages,
		isLoading,
		streamingText,
		pendingAction,
		followUps,
		thinkingSteps,
		sendMessage,
		confirmAction,
		cancelAction,
		clearChat,
	} = useKodaSocket()

	// Every message states which page and student are open; the backend decides what that
	// means, so there is nothing to guess here.
	const sendWithContext = useCallback(
		(text, override) =>
			sendMessage(
				text,
				kodaContextForSend({
					pathname: location.pathname,
					pageContext: chatWidgetStore.pageContext,
					override,
				}),
			),
		[sendMessage, location.pathname],
	)

	// Drain a queued prompt (e.g. from "Ask Koda") once the panel is open and
	// the Koda socket is connected.
	useEffect(() => {
		if (!isOpen) return
		if (connectionStatus !== 'connected') return
		if (!chatWidgetStore.hasPendingSend) return

		const payload = chatWidgetStore.consumePendingSend()
		if (payload?.text) {
			setShowNewChatQuickActions(false)
			sendWithContext(payload.text, payload.kodaContext || undefined)
		}
	}, [isOpen, connectionStatus, messages.length, sendWithContext])

	useEffect(() => {
		const tpl = messageTemplate[location.pathname] || messageTemplate.default
		const timer1 = setTimeout(() => {
			setShowPopover(true)
		}, tpl.time1)

		const timer2 = setTimeout(() => {
			setShowPopover(false)
			setTypingComplete(false)
			setShowBadge(true)
			if (isOpen) {
				setShowBadge(false)
			}
		}, tpl.time2)

		const timer3 = setTimeout(() => {
			setShowBadge(false)
			setTypingComplete(false)
		}, tpl.time1 + tpl.time2)

		return () => {
			clearTimeout(timer1)
			clearTimeout(timer2)
			clearTimeout(timer3)
			setShowPopover(false)
			setShowBadge(false)
			setTypingComplete(false)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname])

	useEffect(() => {
		const handleKodaPrompt = event => {
			const message = String(event?.detail?.message || '').trim()
			if (!message) return
			if (event?.detail?.open) {
				setIsOpen(true)
				setShowBadge(false)
				setShowPopover(false)
			}
			setInputText(message)
			window.setTimeout(() => inputRef.current?.focus(), 0)
		}

		window.addEventListener('counselor-koda-prompt', handleKodaPrompt)
		return () => {
			window.removeEventListener('counselor-koda-prompt', handleKodaPrompt)
		}
	}, [setIsOpen])

	// ---- Speech recognition setup ----
	useEffect(() => {
		const SpeechRecognition =
			window.SpeechRecognition || window.webkitSpeechRecognition
		if (!SpeechRecognition) return

		const existing = recognitionRef.current
		if (existing) {
			try {
				existing.abort()
			} catch (_) {
				/* ignore */
			}
			recognitionRef.current = null
		}

		setIsMicSupported(true)
		const recognition = new SpeechRecognition()
		recognition.continuous = true
		recognition.lang = 'en-US'
		recognition.interimResults = true

		const RESTART_DELAY_MS = 300

		const scheduleRecognitionRestart = () => {
			if (!listeningIntentRef.current) return

			clearRecognitionRestart()
			setIsListening(true)

			recognitionRestartTimerRef.current = setTimeout(() => {
				recognitionRestartTimerRef.current = null
				if (!listeningIntentRef.current) return

				try {
					recognition.start()
				} catch (err) {
					// Chrome needs a gap after onend/no-speech before start() is valid again.
					if (listeningIntentRef.current) {
						scheduleRecognitionRestart()
					}
				}
			}, RESTART_DELAY_MS)
		}

		const stopListeningRecognition = () => {
			listeningIntentRef.current = false
			clearRecognitionRestart()
			setIsListening(false)
			try {
				recognition.stop()
			} catch (_) {
				try {
					recognition.abort()
				} catch (_) {
					/* ignore */
				}
			}
		}

		recognition.onstart = () => {
			if (!listeningIntentRef.current) return
			setIsListening(true)
		}
		recognition.onend = () => {
			if (!listeningIntentRef.current) {
				setIsListening(false)
				return
			}
			scheduleRecognitionRestart()
		}
		recognition.onerror = event => {
			const code = event.error
			if (
				listeningIntentRef.current &&
				(code === 'no-speech' || code === 'aborted' || code === 'network')
			) {
				scheduleRecognitionRestart()
				return
			}
			if (code === 'no-speech' || code === 'aborted') return
			console.error('Speech recognition error:', code)
			stopListeningRecognition()
		}

		recognition.onresult = event => {
			let newFinals = ''
			let interim = ''
			for (let i = event.resultIndex; i < event.results.length; i++) {
				const result = event.results[i]
				const t = (result[0] || result.item?.(0))?.transcript ?? ''
				if (result.isFinal) {
					newFinals += t
				} else {
					interim += t
				}
			}
			if (newFinals) {
				committedRef.current += newFinals
			}
			setInputText((committedRef.current + interim).trim())
		}

		recognitionRef.current = recognition

		return () => {
			listeningIntentRef.current = false
			clearRecognitionRestart()
			recognition.onstart = null
			recognition.onend = null
			recognition.onerror = null
			recognition.onresult = null
			try {
				recognition.abort()
			} catch (_) {
				/* ignore */
			}
			if (recognitionRef.current === recognition) {
				recognitionRef.current = null
			}
		}
	}, [clearRecognitionRestart])

	const handleMicClick = useCallback(
		async e => {
			e.preventDefault()
			e.stopPropagation()

			if (isListening || listeningIntentRef.current) {
				listeningIntentRef.current = false
				clearRecognitionRestart()
				setIsListening(false)
				try {
					recognitionRef.current?.stop()
				} catch (_) {
					try {
						recognitionRef.current?.abort()
					} catch (_) {}
				}
				return
			}

			try {
				await navigator.mediaDevices.getUserMedia({ audio: true })
			} catch (err) {
				console.error('Mic permission denied:', err)
				return
			}
			committedRef.current = typeof inputText === 'string' ? inputText : ''
			clearRecognitionRestart()
			listeningIntentRef.current = true
			setIsListening(true)
			try {
				if (recognitionRef.current) {
					recognitionRef.current.start()
				}
			} catch (err) {
				console.error('Speech recognition start error:', err)
				listeningIntentRef.current = false
				setIsListening(false)
			}
		},
		[isListening, inputText, clearRecognitionRestart],
	)

	// Scroll to bottom when messages or activity steps change
	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
	}, [
		messages,
		streamingText,
		pendingAction,
		isLoading,
		followUps,
		thinkingSteps,
	])

	// After new messages (e.g. action result), scroll again once DOM has updated so the latest content is visible
	const prevMsgCountRef = useRef(messages.length)
	useEffect(() => {
		if (messages.length > prevMsgCountRef.current) {
			prevMsgCountRef.current = messages.length
			const t = setTimeout(() => {
				messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
			}, 150)
			return () => clearTimeout(t)
		}
		prevMsgCountRef.current = messages.length
	}, [messages.length])

	// Focus input when chat opens
	useEffect(() => {
		if (isOpen) setTimeout(() => inputRef.current?.focus(), 200)
	}, [isOpen])

	// When panel opens with existing history, scroll to last message
	useEffect(() => {
		if (isOpen && messages.length > 0) {
			const t = setTimeout(
				() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }),
				100,
			)
			return () => clearTimeout(t)
		}
	}, [isOpen, messages.length])

	// Hide quick actions once conversation starts
	useEffect(() => {
		if (messages.length > 0) setShowNewChatQuickActions(false)
	}, [messages.length])

	const handleSubmit = useCallback(
		e => {
			e?.preventDefault()
			if (!inputText.trim() || isLoading) return
			if (isListening || listeningIntentRef.current) {
				listeningIntentRef.current = false
				clearRecognitionRestart()
				setIsListening(false)
				try {
					recognitionRef.current?.stop()
				} catch (_) {
					/* ignore */
				}
			}
			sendWithContext(inputText)
			setInputText('')
			committedRef.current = ''
		},
		[inputText, isLoading, isListening, sendWithContext, clearRecognitionRestart],
	)

	const handleQuickAction = useCallback(prompt => {
		setInputText(prompt)
		setTimeout(() => inputRef.current?.focus(), 50)
	}, [])

	const handleFollowUpSelect = useCallback(
		text => sendWithContext(text),
		[sendWithContext],
	)
	// eslint-disable-next-line no-unused-vars
	const handleNewChat = useCallback(() => {
		clearChat()
		setShowNewChatQuickActions(true)
	}, [clearChat])

	const handleKeyDown = useCallback(
		e => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault()
				handleSubmit()
			}
		},
		[handleSubmit],
	)

	return (
		<>
			{/* Floating Action Button */}
			{!isOpen && (
				<div className={s.fabWrap}>
					{showPopover && (
						<ChatPopover
							messageTemplate={messageTemplate}
							locationPathname={location.pathname}
							setShowPopover={setShowPopover}
							setTypingComplete={setTypingComplete}
							setInputText={setInputText}
						/>
					)}
					<div className={s.fabInner}>
						<button
							type="button"
							onClick={() => setIsOpen(true)}
							className={s.fabButton}
						>
							<img src={kodaLogo} alt="Koda AI" className={s.fabAvatar} />
						</button>
						{showBadge && (
							<div className={s.fabBadge}>
								<span>1</span>
							</div>
						)}
					</div>
				</div>
			)}

			{/* Chat Panel - right side, doesn't block dashboard */}
			{isOpen && (
				<div className={s.chatPanel}>
					{/* Header */}
					<header className={s.header}>
						<div className={s.headerInner}>
							<div className={s.headerLeft}>
								<div className={s.avatarWrapper}>
									<img src={kodaLogoAsk} alt="Koda" />
								</div>
								<div className={s.headerTitleBlock}>
									<h5 className={s.headerTitle}>
										KODA <span>AI</span>
										<span className={s.sparkle}>
											<StarsIcon />
										</span>
									</h5>
								</div>
								{isChatUnavailable && (
									<span className={s.offlineBadge}>Offline</span>
								)}
							</div>

							<div className={s.headerRight}>
								{/* <button
									className={s.headerBtn}
									onClick={handleNewChat}
									title="New chat"
								>
									<PlusIcon />
								</button> */}
								<button
									className={s.headerBtn}
									onClick={() => setIsOpen(false)}
									title="Close"
								>
									<CrossIcon />
								</button>
							</div>
						</div>
					</header>
					<div className={s.info}>
						<span className={s.info__icon}>
							<MagicIcon />
						</span>
						<span className={s.info__text}>
							Ask koda to perform tasks on the platform
						</span>
					</div>

					{/* Messages Area */}
					<div className={s.messagesArea}>
						{/* Empty state */}
						{messages.length === 0 && !isLoading && (
							<div className={s.empty}>
								<div className={s.empty__inner}>
									<div className={s.empty__icon}>
										<img src={kodaLogoAsk} alt="" />
									</div>
									<h3 className={s.empty__title}>Ask Koda to do something</h3>
									<div className={s.empty__actions}>
										{/* <EmptyAction icon="📅" label="Book Session" /> */}
										<EmptyAction icon="🔍" label="Explore Students" />
										<EmptyAction icon="📝" label="Assignments" />
										<EmptyAction icon="✉️" label="Messages" />
									</div>
									<p className={s.empty__hint}>
										Nothing happens without your confirmation.
									</p>
								</div>
								<div className={s.example}>
									<div className={s.example__title}>
										<span className={s.example__icon}>
											<BlocksIcon />
										</span>
										<span className={s.example__label}>Example Prompts</span>
									</div>
									<button
										className={s.example__btn}
										onClick={() =>
											handleFollowUpSelect(
												'Notify parents of students missing documents',
											)
										}
									>
										Notify parents of students missing documents
									</button>
									<button
										className={s.example__btn}
										onClick={() =>
											handleFollowUpSelect(
												'Assign the personal statement task to a student',
											)
										}
									>
										Assign the personal statement task to a student
									</button>
									<button
										className={s.example__btn}
										onClick={() =>
											handleFollowUpSelect(
												'Schedule a counselling session with a student',
											)
										}
									>
										Schedule a counselling session with a student
									</button>
								</div>
							</div>
						)}

						{/* Message list */}
						{messages.map(msg => (
							<MessageBubble key={msg.id} message={msg} />
						))}

						{/* Activity card - only when there are real tool steps (avoids glitchy box at end for streaming-only responses) */}
						{thinkingSteps.length > 0 && (
							<ActivityCard isLoading={isLoading} steps={thinkingSteps} />
						)}

						{/* Thinking indicator */}
						<ThinkingIndicator isLoading={isLoading} />

						{/* Streaming response */}
						{streamingText && <StreamingBubble text={streamingText} />}

						{/* Confirmation card */}
						{pendingAction && (
							<div className={s.confirmationWrap}>
								<ConfirmationCard
									pendingAction={pendingAction}
									onConfirm={confirmAction}
									onCancel={cancelAction}
								/>
							</div>
						)}

						{/* Follow-up suggestions */}
						{!isLoading && followUps.length > 0 && (
							<FollowUpSuggestions
								suggestions={followUps}
								onSelect={handleFollowUpSelect}
							/>
						)}

						<div ref={messagesEndRef} />
					</div>

					{/* Footer */}
					<div className={s.footer}>
						{(showNewChatQuickActions || messages.length === 0) && (
							<QuickActions onAction={handleQuickAction} />
						)}

						<form className={s.form} onSubmit={handleSubmit}>
							<div className={s.inputWrapper}>
								{/* Attach stub — no upload/attachment path on FE or API yet
								<button type="button" className={s.addBtn} title="Attach">
									<svg
										width="18"
										height="18"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
									>
										<line x1="12" y1="5" x2="12" y2="19" />
										<line x1="5" y1="12" x2="19" y2="12" />
									</svg>
								</button>
								*/}

								<input
									ref={inputRef}
									type="text"
									placeholder="Ask to make a Student list"
									className={s.input}
									value={inputText}
									onChange={e => setInputText(e.target.value)}
									onKeyDown={handleKeyDown}
									disabled={isLoading}
								/>

								{/* Voice input */}
								{isMicSupported && (
									<button
										type="button"
										className={`${s.micBtn} ${isListening ? s.micListening : ''}`}
										onClick={handleMicClick}
										title={isListening ? 'Stop listening' : 'Speak'}
									>
										<MicIcon />
									</button>
								)}

								{/* Send */}
								<button
									type="submit"
									className={`${s.sendBtn} ${inputText.trim() ? s.sendBtnActive : ''}`}
									disabled={!inputText.trim() || isLoading}
								>
									<SendIcon />
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</>
	)
}

function EmptyAction({ icon, label }) {
	return (
		<div className={s.emptyActionItem}>
			<span className={s.emptyActionIcon}>{icon}</span>
			<span className={s.emptyActionLabel}>{label}</span>
		</div>
	)
}

export default observer(ChatWidget)
